/* eslint-disable react-hooks/exhaustive-deps */
import { useDispatch, useSelector } from "react-redux";
import { selectEditMode } from "../slices/editMode/editModeSlice";
import { resourceUpdated, selectResourceById } from "../slices/resourceCollection/resourceCollectionSlice";
import { selectAllResourceChanges } from "../slices/resourceChangeReport/resourceChangeReportSlice";
import { memo, useEffect, useState } from "react";
import { changeTopNavigationElements } from "../slices/navigationPanel/navigationPanelSlice";
import { changeTabMenu } from "../slices/tabPane/tabPaneSlice";
import ReferenceEntry, { DisplayFromFHIRReference } from "../datatypeComponents/ReferenceEntry";
import { displayStringFromFHIRIdentifier } from "../datatypeComponents/IdentifierEntry";
import CodeableConceptEntry, { displayStringFromFHIRCodeableConcept } from "../datatypeComponents/CodeableConceptEntry";
import MarkdownEntry, { DisplayFromFHIRMarkdown } from "../datatypeComponents/MarkdownEntry";
import AttachmentEntry, { DisplayFromFHIRAttachment } from "../datatypeComponents/AttachmentEntry";
import updateChangeReport from "../slices/resourceChangeReport/updateChangeReport";
import updateElement from "../functions/updateElement";
import Spinner from "../components/Spinner";
import ArrayEntry from "../datatypeComponents/ArrayEntry";
import StringEntry from "../datatypeComponents/StringEntry";
import CodeEntry from "../datatypeComponents/CodeEntry";
import ExpandToAddOrEdit from "../components/ExpandToAddOrEdit";

const documentReferenceTypeValueSet = [
    {
        "coding": [{
            "system": "http://hl7.org/fhir/uv/ebm/CodeSystem/research-study-document-types-code-system",
            "code": "statistical-analysis-plan",
            "display": "Statistical analysis plan"
        }
        ]
    },
    {
        "coding": [{
            "system": "http://hl7.org/fhir/uv/ebm/CodeSystem/research-study-document-types-code-system",
            "code": "data-management-plan",
            "display": "Data management plan"
        }
        ]
    },
    {
        "coding": [{
            "system": "http://hl7.org/fhir/uv/ebm/CodeSystem/research-study-document-types-code-system",
            "code": "interim-analysis-plan",
            "display": "Interim analysis plan"
        }
        ]
    },
    {
        "coding": [{
            "system": "http://hl7.org/fhir/uv/ebm/CodeSystem/research-study-document-types-code-system",
            "code": "adverse-event-monitoring-plan",
            "display": "Adverse event monitoring plan"
        }
        ]
    },
    {
        "coding": [{
            "system": "http://hl7.org/fhir/uv/ebm/CodeSystem/research-study-document-types-code-system",
            "code": "auditing-plan",
            "display": "Auditing plan"
        }
        ]
    },
    {
        "coding": [{
            "system": "http://hl7.org/fhir/uv/ebm/CodeSystem/research-study-document-types-code-system",
            "code": "irb-approval",
            "display": "IRB approval"
        }
        ]
    },
    {
        "coding": [{
            "system": "http://hl7.org/fhir/uv/ebm/CodeSystem/research-study-document-types-code-system",
            "code": "dissemination-plan",
            "display": "Dissemination plan"
        }
        ]
    },
    {
        "coding": [{
            "system": "http://hl7.org/fhir/uv/ebm/CodeSystem/research-study-document-types-code-system",
            "code": "consent-form",
            "display": "Consent form"
        }
        ]
    },
    {
        "coding": [{
            "system": "http://hl7.org/fhir/uv/ebm/CodeSystem/research-study-document-types-code-system",
            "code": "consent-plan",
            "display": "Consent plan"
        }
        ]
    },
    {
        "coding": [{
            "system": "http://hl7.org/fhir/uv/ebm/CodeSystem/research-study-document-types-code-system",
            "code": "confidential-personal-data-management-plan",
            "display": "Confidential personal data management plan"
        }
        ]
    },
    {
        "coding": [{
            "system": "http://hl7.org/fhir/uv/ebm/CodeSystem/research-study-document-types-code-system",
            "code": "declaration-of-interests",
            "display": "Declaration of interests"
        }
        ]
    },
    {
        "coding": [{
            "system": "http://hl7.org/fhir/uv/ebm/CodeSystem/research-study-document-types-code-system",
            "code": "data-access-plan",
            "display": "Data access plan"
        }
        ]
    },
    {
        "coding": [{
            "system": "http://hl7.org/fhir/uv/ebm/CodeSystem/research-study-document-types-code-system",
            "code": "ancillary-care-plan",
            "display": "Ancillary care plan"
        }
        ]
    },
    {
        "coding": [{
            "system": "http://hl7.org/fhir/uv/ebm/CodeSystem/research-study-document-types-code-system",
            "code": "dataset",
            "display": "Dataset"
        }
        ]
    },
    {
        "coding": [{
            "system": "http://hl7.org/fhir/uv/ebm/CodeSystem/research-study-document-types-code-system",
            "code": "dataset-access",
            "display": "Dataset access"
        }
        ]
    },
    {
        "coding": [{
            "system": "http://hl7.org/fhir/uv/ebm/CodeSystem/research-study-document-types-code-system",
            "code": "software",
            "display": "Software"
        }
        ]
    },
    {
        "coding": [{
            "system": "http://hl7.org/fhir/uv/ebm/CodeSystem/research-study-document-types-code-system",
            "code": "software-access",
            "display": "Software access"
        }
        ]
    },
    {
        "coding": [{
            "system": "http://hl7.org/fhir/uv/ebm/CodeSystem/research-study-document-types-code-system",
            "code": "education",
            "display": "Education"
        }
        ]
    },
    {
        "coding": [{
            "system": "http://hl7.org/fhir/uv/ebm/CodeSystem/research-study-document-types-code-system",
            "code": "specimen-plan",
            "display": "Specimen plan"
        }
        ]
    }
];


const DocumentReferenceContentEntry = memo(({ elementName, fieldLabel, startingValue, setResourceState,
  startCollapsed }) => {
  let startingDocumentReferenceContent = {
    "attachment": ""
  };
  if (!startingValue) {
    startingValue = "";
  } else {
    if (startingValue.extension) { startingDocumentReferenceContent.extension = startingValue.extension; }
    if (startingValue.attachment) { startingDocumentReferenceContent.attachment = startingValue.attachment; }
    if (startingValue.profile) { startingDocumentReferenceContent.profile = startingValue.profile; }
  }

  const [documentReferenceContentState, setDocumentReferenceContentState] = useState(JSON.parse(JSON.stringify(startingDocumentReferenceContent || {})));
  const [startCollapsedState, setStartCollapsedState] = useState(startCollapsed);

  const updateResourceState = () => {
    let newDocumentReferenceContent = {};
    if (documentReferenceContentState.extension && Object.keys(documentReferenceContentState.extension).length) {
      newDocumentReferenceContent.extension = documentReferenceContentState.extension;
    }
    if (documentReferenceContentState.attachment && Object.keys(documentReferenceContentState.attachment).length) {
      newDocumentReferenceContent.attachment = documentReferenceContentState.attachment;
    }
    if (documentReferenceContentState.profile && Object.keys(documentReferenceContentState.profile).length) {
      newDocumentReferenceContent.profile = documentReferenceContentState.profile;
    }

    if (Object.keys(newDocumentReferenceContent).length === 0) {
      newDocumentReferenceContent = null;
    }
    setResourceState(prevState => { return { ...prevState, [elementName]: newDocumentReferenceContent } });
  }

  useEffect((() => {
    if (Object.keys(documentReferenceContentState).length) {
      updateResourceState();
    }
  }), [documentReferenceContentState]);

  if (startCollapsedState) {
    return <>
      <div>
        <b>{fieldLabel}: </b>
        {startingValue && <DisplayFromFHIRAttachment attachment={startingValue.attachment} />}
        &nbsp;&nbsp;
        <ExpandToAddOrEdit startingValue={startingValue} setStartCollapsedState={setStartCollapsedState} />
      </div>
    </>
  } else {
    return <>
      <p style={{ marginBottom: "2px" }}><b>{fieldLabel}: </b></p>
      <div style={{ marginLeft: "24px" }}>
        <AttachmentEntry elementName='attachment' setResourceState={setDocumentReferenceContentState}
          startCollapsed={documentReferenceContentState.attachment}
          fieldLabel={'Attachment for ' + fieldLabel} startingValue={documentReferenceContentState.attachment} />
      </div>
    </>
  }
})

const DocumentReferenceDisplay = ({ foi }) => {

    const dispatch = useDispatch();
    const editMode = useSelector(state => selectEditMode(state));
    const parentResourceDetail = useSelector(state => selectResourceById(state, foi));
    const resourceChanges = useSelector(state => selectAllResourceChanges(state));
    const resourceJson = parentResourceDetail?.revisedResourceJson;

    let displayTitle;
    if (Array.isArray(resourceJson.extension)) {
        for (let extension of resourceJson.extension) {
            if (extension.url === 'http://hl7.org/fhir/StructureDefinition/artifact-title') {
                if (extension.valueString) {
                    displayTitle = extension.valueString;
                }
            }
        }
    }
    if (resourceJson.title) {
        displayTitle = resourceJson.title;
    }

    const [documentReferenceResourceState, setDocumentReferenceResourceState] = useState({});
    const [extensionState, setExtensionState] = useState({ "otherExtensions": [], "title": "" });
    const [startingExtensionState, setStartingExtensionState] = useState({ "otherExtensions": [], "title": "" });

    useEffect(() => {
        if (resourceJson) {
            let resourceJsonCopy = JSON.parse(JSON.stringify(resourceJson));
            if (!resourceJsonCopy.content || !Array.isArray(resourceJsonCopy.content) || resourceJsonCopy.content.length === 0) {
                resourceJsonCopy.content = [{ "attachment": "" }];
            }
            setDocumentReferenceResourceState(resourceJsonCopy);
            dispatch(changeTopNavigationElements(["Content", "Description", "Identification", "Subject", "Stewardship"]));
            dispatch(changeTabMenu(["Text View", "JSON View", "Usage View"]));
            let startingExtension = resourceJson.extension || [];
            let startingExtensionStateValue = { "otherExtensions": [], "title": resourceJson.title || "" };
            if (Array.isArray(startingExtension) && startingExtension.length > 0) {
                for (const extension of startingExtension) {
                    switch (extension.url) {
                        case 'http://hl7.org/fhir/StructureDefinition/artifact-title':
                            startingExtensionStateValue.title = extension.valueString;
                            break;
                        default:
                            startingExtensionStateValue.otherExtensions.push(extension);
                    }
                }
            }
            setExtensionState(JSON.parse(JSON.stringify(startingExtensionStateValue)));
            setStartingExtensionState(JSON.parse(JSON.stringify(startingExtensionStateValue)));
        }
    }, [resourceJson]);

    useEffect(() => {
        if (extensionState && JSON.stringify(extensionState) !== JSON.stringify(startingExtensionState)) {
            let newExtension = [];
            if (extensionState.otherExtensions) {
                newExtension = JSON.parse(JSON.stringify(extensionState.otherExtensions));
            }
            if (extensionState.title) {
                newExtension.push({
                    "url": "http://hl7.org/fhir/StructureDefinition/artifact-title",
                    "valueString": extensionState.title
                });
            }
            if (!(newExtension.length > 0)) {
                newExtension = null;
            }
            setDocumentReferenceResourceState(prevState => { return { ...prevState, extension: newExtension }; });
        }
    }, [extensionState.title]);

    useEffect(() => {
        if (editMode && resourceJson) {
            let newResourceDetail = JSON.parse(JSON.stringify(parentResourceDetail));
            let previousInitialValue = null;
            let previousFinalValue = null;
            previousInitialValue = newResourceDetail.resourceJson.extension;
            previousFinalValue = newResourceDetail.revisedResourceJson.extension;
            newResourceDetail.revisedResourceJson.extension = documentReferenceResourceState.extension;
            let displayTitle = newResourceDetail.displayTitle;
            for (const extension of documentReferenceResourceState.extension) {
                if (extension.url === 'http://hl7.org/fhir/StructureDefinition/artifact-title' && extension.valueString) {
                    displayTitle = extension.valueString;
                }
            }
            newResourceDetail.displayTitle = displayTitle || documentReferenceResourceState.title;
            if (documentReferenceResourceState.extension !== undefined &&
                JSON.stringify(previousFinalValue) !== JSON.stringify(documentReferenceResourceState.extension)) {
                console.log("resourceUpdate for DocumentReference.extension");
                dispatch(resourceUpdated(newResourceDetail));
                updateChangeReport(dispatch, resourceChanges, newResourceDetail.id,
                    "ArrayOfExtension",
                    "extension",
                    previousInitialValue,
                    documentReferenceResourceState.extension);
            }
        }
    }, [documentReferenceResourceState.extension]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(documentReferenceResourceState, "type", "CodeableConcept", parentResourceDetail,
                resourceChanges, dispatch, resourceUpdated, updateChangeReport);
        }
    }, [documentReferenceResourceState.type]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(documentReferenceResourceState, "content", "ArrayOfDocumentReferenceContent", parentResourceDetail,
                resourceChanges, dispatch, resourceUpdated, updateChangeReport);
        }
    }, [documentReferenceResourceState.content]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(documentReferenceResourceState, "status", "code", parentResourceDetail,
                resourceChanges, dispatch, resourceUpdated, updateChangeReport);
        }
    }, [documentReferenceResourceState.status]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(documentReferenceResourceState, "docStatus", "code", parentResourceDetail,
                resourceChanges, dispatch, resourceUpdated, updateChangeReport);
        }
    }, [documentReferenceResourceState.docStatus]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(documentReferenceResourceState, "description", "markdown", parentResourceDetail,
                resourceChanges, dispatch, resourceUpdated, updateChangeReport);
        }
    }, [documentReferenceResourceState.description]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(documentReferenceResourceState, "category", "ArrayOfCodeableConcept", parentResourceDetail,
                resourceChanges, dispatch, resourceUpdated, updateChangeReport);
        }
    }, [documentReferenceResourceState.category]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(documentReferenceResourceState, "identifier", "ArrayOfIdentifier", parentResourceDetail,
                resourceChanges, dispatch, resourceUpdated, updateChangeReport);
        }
    }, [documentReferenceResourceState.identifier]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(documentReferenceResourceState, "version", "string", parentResourceDetail,
                resourceChanges, dispatch, resourceUpdated, updateChangeReport);
        }
    }, [documentReferenceResourceState.version]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(documentReferenceResourceState, "subject", "Reference", parentResourceDetail,
                resourceChanges, dispatch, resourceUpdated, updateChangeReport);
        }
    }, [documentReferenceResourceState.subject]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(documentReferenceResourceState, "related", "ArrayOfReference", parentResourceDetail,
                resourceChanges, dispatch, resourceUpdated, updateChangeReport);
        }
    }, [documentReferenceResourceState.related]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(documentReferenceResourceState, "author", "ArrayOfReference", parentResourceDetail,
                resourceChanges, dispatch, resourceUpdated, updateChangeReport);
        }
    }, [documentReferenceResourceState.author]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(documentReferenceResourceState, "custodian", "Reference", parentResourceDetail,
                resourceChanges, dispatch, resourceUpdated, updateChangeReport);
        }
    }, [documentReferenceResourceState.custodian]);

    if (!resourceJson) {
        return <Spinner />;
    }

    return <div>
        <div style={{ marginTop: "12px" }}>
            <h3 id="content">Content</h3>
            <div style={{ marginLeft: "24px" }}>
                {editMode ?
                    <>
                        <StringEntry elementName='title' fieldLabel='Title'
                            startingValue={extensionState.title}
                            setResourceState={setExtensionState} />
                    </>
                    :
                    <>
                        {displayTitle && <p><b>Title: </b>{displayTitle}</p>}
                    </>
                }
                {editMode ?
                    <>
                        <CodeableConceptEntry elementName='type' fieldLabel='Type'
                            startingValue={documentReferenceResourceState.type}
                            codeableConceptLevelValueSet={documentReferenceTypeValueSet}
                            setResourceState={setDocumentReferenceResourceState} />
                    </>
                    :
                    <>
                        {resourceJson.type && <p><b>Type: </b>{displayStringFromFHIRCodeableConcept(resourceJson.type)}</p>}
                    </>
                }
                {editMode ?
                    <>
                        <ArrayEntry datatype="DocumentReferenceContent" elementName='content'
                            fieldLabel='Content (as Attachment)'
                            startingValue={documentReferenceResourceState.content || []}
                            setResourceState={setDocumentReferenceResourceState} />
                    </>
                    :
                    <>
                        {(Array.isArray(resourceJson.content) &&
                            resourceJson.content.length) && resourceJson.content.map((content, index) => {
                                return <div key={index}>
                                    <DisplayFromFHIRAttachment attachment={content.attachment} />
                                </div>
                            })}
                    </>
                }
                {editMode ?
                    <>
                        <CodeEntry elementName='status' fieldLabel='Resource status'
                            startingValue={documentReferenceResourceState.status || 'current'}
                            allowedValues={['current', 'superseded', 'entered-in-error']}
                            setResourceState={setDocumentReferenceResourceState} />
                    </>
                    :
                    <>
                        {resourceJson.status && <p><b>Resource status: </b>{resourceJson.status}</p>}
                    </>
                }
                {editMode ?
                    <>
                        <CodeEntry elementName='docStatus' fieldLabel='Document status'
                            startingValue={documentReferenceResourceState.docStatus}
                            allowedValues={['registered', 'partial', 'preliminary', 'final', 'amended', 'corrected', 'appended', 'cancelled', 'entered-in-error', 'deprecated', 'unknown']}
                            setResourceState={setDocumentReferenceResourceState} />
                    </>
                    :
                    <>
                        {resourceJson.docStatus && <p><b>Document status: </b>{resourceJson.docStatus}</p>}
                    </>
                }
            </div>
            <h3 id="description">Description</h3>
            <div style={{ marginLeft: "24px" }}>
                {editMode ?
                    <>
                        <MarkdownEntry elementName='description' fieldLabel='Description'
                            startingValue={documentReferenceResourceState.description} startCollapsed
                            setResourceState={setDocumentReferenceResourceState} />
                    </>
                    :
                    <>
                        {resourceJson.description &&
                            <div><b>Description: </b><DisplayFromFHIRMarkdown markdown={resourceJson.description} /></div>}
                    </>
                }
                {editMode ?
                    <>
                        <ArrayEntry datatype="CodeableConcept" elementName='category'
                            fieldLabel='Category' startCollapsed
                            startingValue={documentReferenceResourceState.category || []}
                            setResourceState={setDocumentReferenceResourceState} />
                    </>
                    :
                    <>
                        {(resourceJson.category?.length > 1) && resourceJson.category.map((category, identifierIndex) => {
                            return <div key={identifierIndex}>
                                <p><b>Category: </b>{displayStringFromFHIRCodeableConcept(category)}</p>
                            </div>
                        })}
                    </>
                }
            </div>
            <h3 id="identification">Identification</h3>
            <div style={{ marginLeft: "24px" }}>
                {editMode ?
                    <>
                        <ArrayEntry datatype="Identifier" elementName='identifier'
                            fieldLabel='Identifier' startCollapsed
                            startingValue={documentReferenceResourceState.identifier || []}
                            setResourceState={setDocumentReferenceResourceState} />
                    </>
                    :
                    <>
                        {(resourceJson.identifier?.length > 1) &&
                            resourceJson.identifier.map((identifier, identifierIndex) => {
                                return <div key={identifierIndex}>
                                    <p><b>Identifier: </b>{displayStringFromFHIRIdentifier(identifier)}</p>
                                </div>
                            })}
                    </>
                }
                {editMode ?
                    <>
                        <StringEntry elementName='version' fieldLabel='Version'
                            startingValue={documentReferenceResourceState.version}
                            setResourceState={setDocumentReferenceResourceState} />
                    </>
                    :
                    <>
                        {resourceJson.version && <p><b>Version: </b>{resourceJson.version}</p>}
                    </>
                }
            </div>
            <h3 id="subject">Subject</h3>
            <div style={{ marginLeft: "24px" }}>
                {editMode ?
                    <>
                        <ReferenceEntry elementName='subject'
                            fieldLabel='Subject'
                            startingValue={documentReferenceResourceState.subject || null}
                            enableCreation={true} startCollapsed
                            setResourceState={setDocumentReferenceResourceState} />
                    </>
                    :
                    <>
                        {resourceJson.subject &&
                            <div><b>Subject: </b><DisplayFromFHIRReference reference={resourceJson.subject} /></div>}
                    </>
                }
                {editMode ?
                    <>
                        <ArrayEntry datatype="Reference" elementName='related'
                            fieldLabel='Related Item'
                            startingValue={documentReferenceResourceState.related || []}
                            enableCreation={true} startCollapsed
                            setResourceState={setDocumentReferenceResourceState} />
                    </>
                    :
                    <>
                        {(resourceJson.related?.length > 1) && resourceJson.related.map((related, relatedIndex) => {
                            return <div key={relatedIndex}>
                                <div><b>Related Item: </b><DisplayFromFHIRReference reference={related} /></div>
                            </div>
                        })}
                    </>
                }
            </div>
            <h3 id="stewardship">Stewardship</h3>
            <div style={{ marginLeft: "24px" }}>
                {editMode ?
                    <>
                        <ArrayEntry datatype="Reference" elementName='author'
                            fieldLabel='Author'
                            referencedResourceTypes={["Practitioner", "PractitionerRole", "Organization", "Device", "Patient", "RelatedPerson", "CareTeam", "Group"]}
                            startingValue={documentReferenceResourceState.author || []}
                            setResourceState={setDocumentReferenceResourceState}
                            enableCreation={true} startCollapsed />
                    </>
                    :
                    <>
                        {(resourceJson.author?.length > 1) && resourceJson.author.map((author, authorIndex) => {
                            return <div key={authorIndex}>
                                <div><b>Author: </b><DisplayFromFHIRReference reference={author} /></div>
                            </div>
                        })}
                    </>
                }
                {editMode ?
                    <>
                        <ReferenceEntry elementName='custodian'
                            fieldLabel='Custodian' startingResourceType="Organization"
                            enableCreation={true} startCollapsed
                            startingValue={documentReferenceResourceState.custodian || null}
                            setResourceState={setDocumentReferenceResourceState} />
                    </>
                    :
                    <>
                        {resourceJson.custodian &&
                            <div><b>Custodian: </b><DisplayFromFHIRReference reference={resourceJson.custodian} /></div>}
                    </>
                }
            </div>
        </div>
    </div>
}

export default DocumentReferenceDisplay;
export { DocumentReferenceContentEntry };